import React, { useEffect, useState } from 'react'
import { useFormState } from 'react-hook-form'
import { Button, IconClose, Typography } from '@grammarly/badger-ui'

import { useUserService } from '../../../lib/hooks'
import { TypeComponentCtaButton } from '../../../lib/types'
import { TypeComponentTextInput } from '../../../lib/types/component-text-input'
import { InputError } from '../../inputs/hooks'
import { charactersToWords } from '../../inputs/validations'
import { ContentProps, ContentRenderer } from '../../renderer'
import { Modal } from '../../ui-lib/modal'
import { Card } from './card'

import cardStyles from './card/card.module.scss'
import styles from './go-templates-tool.module.scss'

const { HS, C4 } = Typography

interface AnonymousTextLimitModalProps extends ContentProps<TypeComponentTextInput> {
  name: string
  isSignUpWallOpen: boolean
  anonymousCharacterWordLimitCta?: TypeComponentCtaButton
}

export const AnonymousTextLimitModal: React.FC<AnonymousTextLimitModalProps> = ({
  structure: {
    fields: { anonymousCharacterWordLimitCta, anonymousCharacterLimit, useWordLimit },
  },
  name,
  isSignUpWallOpen,
}) => {
  const [isWallOpen, setIsWallOpen] = useState(false)
  const [hasOpenedWall, setHasOpenedWall] = useState(false)
  const { errors, isValidating } = useFormState({ name })
  const { isAnonymous } = useUserService().currentUser

  useEffect(() => {
    if (isSignUpWallOpen) {
      setIsWallOpen(false)
    } else if (errors[name]?.message?.toString() === InputError.ExceededAnonymousLimit && !hasOpenedWall) {
      setIsWallOpen(true)
      setHasOpenedWall(true)
    }
  }, [isSignUpWallOpen, setIsWallOpen, errors, name, hasOpenedWall, setHasOpenedWall, isValidating])

  if (!anonymousCharacterWordLimitCta || !anonymousCharacterLimit || !isAnonymous) {
    return null
  }

  const limitType = useWordLimit ? 'word' : 'character'
  const limit = useWordLimit ? anonymousCharacterLimit / charactersToWords : anonymousCharacterLimit

  return (
    <Modal className={styles.textLimitWallModal} isOpen={isWallOpen} onRequestClose={() => setIsWallOpen(false)}>
      <Card className={styles.signupWall}>
        <Button
          className={styles.signupWallClose}
          padding="none"
          onClick={() => setIsWallOpen(false)}
          aria-label="Close"
        >
          <IconClose aria-hidden={true} />
        </Button>
        <HS as="h2">
          <b>{`This text exceeds the ${limitType} limit`}</b>
        </HS>
        <C4 as="div" className={cardStyles.description}>
          There is a {limit} {limitType} limit for this tool. Create a free account to use Grammarly with longer text.
        </C4>
        <ContentRenderer key={anonymousCharacterWordLimitCta.sys.id} structure={anonymousCharacterWordLimitCta} />
      </Card>
    </Modal>
  )
}
